import { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn, hasConsecutiveDigits, getSubmissionId, getQuoteData } from '@/lib/utils';
import { ChevronRight, ChevronLeft, Check } from 'lucide-react';
import { pushGTMEvent } from '@/lib/gtm';

interface QuestionnaireProps {
  onComplete: (data: {
    age: string;
    gender: string;
    coverage: string;
    firstName: string;
    phoneNumber: string;
    motivations?: string[];
    beneficiary?: string;
    coverageStart?: string;
    health?: string;
  }) => void;
}

const steps = [
  { 
    title: 'What brings you here today?', 
    description: 'Select up to 2 options that apply to you' 
  },
  { 
    title: 'Who are you thinking about?', 
    description: 'When considering life insurance, who comes to mind?' 
  },
  { 
    title: 'What is your coverage timeline?', 
    description: 'When would you like your life insurance coverage to begin?' 
  },
  { 
    title: 'How is your health?', 
    description: 'How would you estimate your current health?' 
  },
  { 
    title: 'What coverage amount are you looking for?', 
    description: 'You can choose any for now. A licensed advisor will get in touch with you to recommend a personalized coverage amount.' 
  },
  { 
    title: "What is your gender?", 
    description: 'This information can affect the quote.' 
  },
  { 
    title: 'What is your age?', 
    description: 'Select the nearest age.' 
  },
  { 
    title: 'What is your first name?', 
    description: 'So we can personalize your insurance quote just for you.' 
  },
  { 
    title: 'What is your phone number?', 
    description: "We'll text you a copy of your life insurance quotes, so you don't lose them." 
  },
];

export function Questionnaire({ onComplete }: QuestionnaireProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [searchParams] = useSearchParams();

  // Initialize form data from URL parameters
  const initFormData = useCallback(() => {
    return {
      age: searchParams.get('age') || '',
      gender: searchParams.get('gender') || '',
      coverage: searchParams.get('coverage') || '',
      firstName: searchParams.get('firstName') || '',
      phoneNumber: searchParams.get('phoneNumber') || '',
      // New fields
      motivations: searchParams.get('motivations') ? searchParams.get('motivations')?.split(',') || [] : [],
      beneficiary: searchParams.get('beneficiary') || '',
      coverageStart: searchParams.get('coverageStart') || '',
      health: searchParams.get('health') || '',
    };
  }, [searchParams]);

  const [formData, setFormData] = useState({
    ...initFormData()
  });

  // Set initial step based on filled form data
  useEffect(() => {
    const data = initFormData();
    
    // Track questionnaire view
    pushGTMEvent({
      event: 'questionnaire_view',
      form_step: currentStep + 1,
      total_steps: steps.length
    });
    
    if (data.phoneNumber) setCurrentStep(8);
    else if (data.firstName) setCurrentStep(7);
    else if (data.age) setCurrentStep(6);
    else if (data.gender) setCurrentStep(5);
    else if (data.coverage) setCurrentStep(4);
    else if (data.health) setCurrentStep(3);
    else if (data.coverageStart) setCurrentStep(2);
    else if (data.beneficiary) setCurrentStep(1);
    else if (data.motivations && data.motivations.length > 0) setCurrentStep(0);
  }, []);

  const [errors, setErrors] = useState<string>('');

  const getProgressMessage = (step: number) => {
    const progress = ((step + 1) / steps.length) * 100;
    if (step === 7) {
      return "Almost there!";
    } else if (step === 8) {
      return "Discover your best rates";
    } else if (progress >= 60) {
      return "Keep going!";
    }
    return `${Math.round(progress)}% completed`;
  };

  const updateFormData = (key: keyof typeof formData, value: any) => {
    // Update form data first
    setFormData(prev => ({ ...prev, [key]: value }));
    setErrors(''); // Clear any existing errors
    
    // Auto-advance for selection steps (except motivations which is multi-select)
    if (key === 'beneficiary' || key === 'coverageStart' || key === 'health' || key === 'coverage' || key === 'gender' || key === 'age') {
      // Use the new value directly for validation
      const isValid = validateStep(key, value);
      if (isValid) {
        setTimeout(() => setCurrentStep(prev => prev + 1), 0);
      }
    }
  };

  const toggleMotivation = (value: string) => {
    setFormData(prev => {
      const currentMotivations = [...(prev.motivations || [])];
      const index = currentMotivations.indexOf(value);
      
      if (index === -1) {
        if (currentMotivations.length < 2) {
          currentMotivations.push(value);
          if (currentMotivations.length === 2) {
            // Auto-advance to Beneficiary step (index 1)
            setTimeout(() => setCurrentStep(1), 500);
          }
        }
      } else {
        // Remove if already selected
        currentMotivations.splice(index, 1);
      }
      
      return {
        ...prev,
        motivations: currentMotivations
      };
    });
    
    setErrors('');
  };

  const validateStep = (key: string, value: any) => {
    switch (key) {
      case 'motivations':
        return Array.isArray(value) && value.length > 0;
      case 'beneficiary':
      case 'coverageStart':
      case 'health':
      case 'age':
      case 'gender':
      case 'coverage':
        return !!value;
      default:
        return false;
    }
  };

  const validatePhoneNumber = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length !== 10) return false;
    return true;
  };

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digits and any leading "1"
    let cleaned = value.replace(/\D/g, '');
    if (cleaned.startsWith('1')) {
      cleaned = cleaned.slice(1);
    }
    // Limit to 10 digits
    cleaned = cleaned.slice(0, 10);
    if (cleaned.length === 0) return '';
    if (cleaned.length <= 3) return `(${cleaned}`;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    if (formatted.length <= 14) { // (XXX) XXX-XXXX = 14 chars
      updateFormData('phoneNumber', formatted);
    }
  };

  const validateCurrentStep = () => {
    switch (currentStep) {
      case 0: // Motivations
        if (!formData.motivations || formData.motivations.length < 1) {
          setErrors('Please select at least 1 option');
          return false;
        }
        break;
      case 1: // Beneficiary
        if (!formData.beneficiary) {
          setErrors('Please select who you are thinking about');
          return false;
        }
        break;
      case 2: // Coverage Start
        if (!formData.coverageStart) {
          setErrors('Please select when you would like coverage to begin');
          return false;
        }
        break;
      case 3: // Health
        if (!formData.health) {
          setErrors('Please describe your current health');
          return false;
        }
        break;
      case 4: // Coverage Amount
        if (!formData.coverage) {
          setErrors('Please select your coverage amount');
          return false;
        }
        break;
      case 5: // Gender
        if (!formData.gender) {
          setErrors('Please select your gender');
          return false;
        }
        break;
      case 6: // Age
        if (!formData.age) {
          setErrors('Please select your nearest age');
          return false;
        }
        break;
      case 7: // First Name
        if (!formData.firstName || formData.firstName.length < 3) {
          setErrors('Please enter your first name (minimum 3 characters)');
          return false;
        }
        break;
      case 8: // Phone Number
        if (!validatePhoneNumber(formData.phoneNumber)) {
          setErrors('Please enter a valid phone number');
          return false;
        }
        break;
    }
    return true;
  };

  const handleNext = () => {
    if (!validateCurrentStep()) return;

    if (currentStep === steps.length - 1) {
      if (formData.firstName.toLowerCase() === 'test' || hasConsecutiveDigits(formData.phoneNumber)) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        onComplete(formData);
        return;
      }
      
      // Get quote data
      const quoteData = getQuoteData(formData.age, formData.gender, formData.coverage);

      // Send webhook and wait for response
      fetch('https://hook.us1.make.com/lt4q3473jpjku41c78vzofgm069xmvpc', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          applicant: {
            first_name: formData.firstName,
            phone: formData.phoneNumber.replace(/\D/g, ''),
            language: 'EN',
            gender: formData.gender,
            age: formData.age,
            coverage_amount: formData.coverage,
            // Add new fields
            motivations: formData.motivations,
            beneficiary: formData.beneficiary,
            coverage_start: formData.coverageStart,
            health: formData.health
          },
          submission_id: getSubmissionId(),
          view_prices: 'NO',
          quote_data: quoteData
        }),
      }).then(async response => {
          const contentType = response.headers.get("content-type");
          if (contentType && contentType.includes("application/json")) {
            return response.json();
          }
          // Handle non-JSON response
          return { status: 'success', message: await response.text() };
        })
        .then(data => {
          // Add the response ID to the URL parameters
          const params = new URLSearchParams(window.location.search);
          params.set('age', formData.age);
          params.set('gender', formData.gender);
          params.set('coverage', formData.coverage);
          params.set('firstName', formData.firstName);
          params.set('phoneNumber', formData.phoneNumber);
          
          // Add new fields to URL parameters
          if (formData.motivations && formData.motivations.length > 0) {
            params.set('motivations', formData.motivations.join(','));
          }
          if (formData.beneficiary) {
            params.set('beneficiary', formData.beneficiary);
          }
          if (formData.coverageStart) {
            params.set('coverageStart', formData.coverageStart);
          }
          if (formData.health) {
            params.set('health', formData.health);
          }
          
          // Only set applicationId if we have a JSON response with an id
          if (data.status !== 'success' && data.id) {
            params.set('applicationId', data.id);
          }
          window.history.replaceState({}, '', `?${params.toString()}`);
          onComplete(formData);
        })
        .catch(error => {
          console.error('Webhook error:', error);
          // Continue with the form submission even if the webhook fails
          const params = new URLSearchParams(window.location.search);
          params.set('age', formData.age);
          params.set('gender', formData.gender);
          params.set('coverage', formData.coverage);
          params.set('firstName', formData.firstName);
          params.set('phoneNumber', formData.phoneNumber);
          
          // Add new fields to URL parameters
          if (formData.motivations && formData.motivations.length > 0) {
            params.set('motivations', formData.motivations.join(','));
          }
          if (formData.beneficiary) {
            params.set('beneficiary', formData.beneficiary);
          }
          if (formData.coverageStart) {
            params.set('coverageStart', formData.coverageStart);
          }
          if (formData.health) {
            params.set('health', formData.health);
          }
          
          window.history.replaceState({}, '', `?${params.toString()}`);
          onComplete(formData);
        });

      // Scroll to top before showing loading
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
    setErrors('');
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0: // Motivations
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3">
              {[
                'Cover funeral expenses',
                'Create a safety net for my loved ones',
                'Make sure no one inherits my debts',
                'I\'m just exploring options',
                'I\'m not sure yet'
              ].map((option) => (
                <button
                  key={option}
                  onClick={() => toggleMotivation(option)}
                  className={cn(
                    'flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 rounded-full text-left transition-all',
                    'border-2 border-gray-200 hover:border-[#494d62] hover:font-bold hover:shadow-lg hover:-translate-y-[3px]',
                    formData.motivations?.includes(option) 
                      ? 'bg-[#494d62] text-white border-[#494d62]' 
                      : 'bg-gray-50 text-[#494d62]'
                  )}
                >
                  <span className="text-sm sm:text-base">{option}</span>
                  {formData.motivations?.includes(option) && (
                    <Check className="h-5 w-5 flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
            {formData.motivations && formData.motivations.length === 2 && (
              <p className="text-sm text-gray-500 text-center mt-2">
                You've selected the maximum of 2 options
              </p>
            )}
          </div>
        );

      case 1: // Beneficiary
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3">
              {[
                'My spouse or partner',
                'My children',
                'A parent',
                'Someone else',
                'Just me'
              ].map((option) => (
                <button
                  key={option}
                  onClick={() => updateFormData('beneficiary', option)}
                  className={cn(
                    'flex items-center justify-between px-6 py-4 rounded-full text-left transition-all',
                    'border-2 border-gray-200 hover:border-[#494d62]',
                    formData.beneficiary === option 
                      ? 'bg-[#494d62] text-white border-[#494d62]' 
                      : 'bg-gray-50 text-[#494d62]'
                  )}
                >
                  <span className="text-base">{option}</span>
                  {formData.beneficiary === option && (
                    <Check className="h-5 w-5 flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          </div>
        );

      case 2: // Coverage Start
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3">
              {[
                'I\'m ready today',
                'In the next week',
                'Within a month or two',
                'I\'m not sure yet'
              ].map((option) => (
                <button
                  key={option}
                  onClick={() => updateFormData('coverageStart', option)}
                  className={cn(
                    'flex items-center justify-between px-6 py-4 rounded-full text-left transition-all',
                    'border-2 border-gray-200 hover:border-[#494d62]',
                    formData.coverageStart === option 
                      ? 'bg-[#494d62] text-white border-[#494d62]' 
                      : 'bg-gray-50 text-[#494d62]'
                  )}
                >
                  <span className="text-base">{option}</span>
                  {formData.coverageStart === option && (
                    <Check className="h-5 w-5 flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          </div>
        );

      case 3: // Health
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3">
              {[
                'Excellent',
                'Good',
                'Average',
                'Prefer not to say'
              ].map((option) => (
                <button
                  key={option}
                  onClick={() => updateFormData('health', option)}
                  className={cn(
                    'flex items-center justify-between px-6 py-4 rounded-full text-left transition-all',
                    'border-2 border-gray-200 hover:border-[#494d62]',
                    formData.health === option 
                      ? 'bg-[#494d62] text-white border-[#494d62]' 
                      : 'bg-gray-50 text-[#494d62]'
                  )}
                >
                  <span className="text-base">{option}</span>
                  {formData.health === option && (
                    <Check className="h-5 w-5 flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          </div>
        );

      case 4: // Coverage Amount
        return (
          <div className="space-y-4">
            <ToggleGroup
              type="single"
              value={formData.coverage}
              onValueChange={(value) => {
                if (value) {
                  updateFormData('coverage', value);
                }
              }}
              className="grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              {[
                { label: '$10,000', value: '10000' },
                { label: '$25,000', value: '25000' },
                { label: '$50,000', value: '50000' },
              ].map(({ label, value }) => (
                <ToggleGroupItem
                  key={value}
                  value={value}
                  className={cn(
                    'text-xl px-8 py-6 rounded-full transition-all bg-gray-50 border border-[#494d62]',
                    'data-[state=on]:bg-[#494d62] data-[state=on]:text-white',
                    'hover:bg-[#494d62] hover:text-white data-[state=on]:hover:bg-primary/90'
                  )}
                >
                  {label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>
        );

      case 5: // Gender
        return (
          <div className="space-y-4">
            <ToggleGroup
              type="single"
              value={formData.gender}
              onValueChange={(value) => {
                if (value) {
                  updateFormData('gender', value);
                }
              }}
              className="grid grid-cols-2 gap-4"
            >
              {['Female', 'Male'].map((gender) => (
                <ToggleGroupItem
                  key={gender}
                  value={gender}
                  className={cn(
                    'text-xl px-8 py-6 rounded-full transition-all bg-gray-50 border border-[#494d62]',
                    'data-[state=on]:bg-[#494d62] data-[state=on]:text-white',
                    'hover:bg-[#494d62] hover:text-white data-[state=on]:hover:bg-primary/90'
                  )}
                >
                  {gender}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>
        );

      case 6: // Age
        return (
          <div className="space-y-4">
            <ToggleGroup
              type="single"
              value={formData.age}
              onValueChange={(value) => {
                if (value) updateFormData('age', value);
              }}
              className="grid grid-cols-4 gap-4"
            >
              {['40', '45', '50', '55', '60', '65', '70', '75'].map((age) => (
                <ToggleGroupItem
                  key={age}
                  value={age}
                  className={cn(
                    'text-xl px-8 py-6 rounded-full transition-all bg-gray-50 border border-[#494d62]',
                    'data-[state=on]:bg-[#494d62] data-[state=on]:text-white',
                    'hover:bg-[#494d62] hover:text-white data-[state=on]:hover:bg-primary/90'
                  )}
                >
                  {age}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>
        );

      case 7: // First Name
        return (
          <div className="space-y-4">
            <input
              autoFocus
              name="given-name"
              autoComplete="given-name"
              type="text"
              placeholder="First Name"
              value={formData.firstName}
              onChange={(e) => updateFormData('firstName', e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && formData.firstName.length >= 3) {
                  handleNext();
                }
              }}
              className="w-full text-xl px-8 py-2.5 h-[52px] rounded-full text-center bg-white border-2 border-gray-200 focus:outline-none focus:border-[#494d62] transition-colors"
            />
          </div>
        );

      case 8: // Phone Number
        return (
          <div className="space-y-4">
            <input
              autoFocus
              name="tel"
              autoComplete="tel-national"
              type="tel"
              inputMode="tel"
              placeholder="(XXX) XXX-XXXX"
              value={formData.phoneNumber}
              onChange={handlePhoneChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && validatePhoneNumber(formData.phoneNumber)) {
                  handleNext();
                }
              }}
              className="w-full text-xl px-8 py-2.5 h-[52px] rounded-full text-center bg-white border-2 border-gray-200 focus:outline-none focus:border-[#494d62] transition-colors"
            />
          </div>
        );
    }
  };

  return (
    <div ref={containerRef} className="max-w-2xl mx-auto bg-white p-4 sm:p-6 md:p-10 rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.06)]">
      <div className="mb-8 space-y-4">
        <div className="flex items-center justify-between text-sm text-[#494d62]">
          <span>Step {currentStep + 1} of {steps.length}</span>
          <span className="font-medium">{getProgressMessage(currentStep)}</span>
        </div>
        <motion.div 
          className="relative w-full overflow-hidden rounded-full bg-[#494d62]/20 h-2"
          initial={{ opacity: 0.8 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div 
            className="h-full bg-[#494d62] transition-all"
            initial={{ width: `${((currentStep) / steps.length) * 100}%` }}
            animate={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
        </motion.div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
          className="space-y-8"
        >
          <div className="text-center space-y-2">
            <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-[#494d62]">
              {steps[currentStep].title}
            </h2>
            <p className="text-sm sm:text-base text-[#494d62]">{steps[currentStep].description}</p>
          </div>

          <div className="min-h-[160px]">
            {renderStep()}
          </div>

          {errors && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-red-500 text-center"
            >
              {errors}
            </motion.p>
          )}

          <div className="flex justify-between items-center pt-4">
            <Button
              variant="ghost"
              onClick={handleBack}
              disabled={currentStep === 0}
              className={cn(
                'gap-2 hover:bg-gray-100',
                currentStep === 0 && 'invisible'
              )}
            >
              <ChevronLeft className="h-4 w-4" />
              Back
            </Button>

            <Button
              onClick={handleNext}
              className="gap-2 px-8 bg-[#494d62] hover:bg-[#494d62]/90"
            >
              {currentStep === steps.length - 1 ? 'Complete' : 'Continue'}
              {currentStep < steps.length - 1 && <ChevronRight className="h-4 w-4" />}
            </Button>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}